import { useState, useEffect } from 'react';
import './WalletsTab.css';
import WalletAnalysisView from './WalletAnalysisView';
import { supabase } from './supabase';

let nextId = 1;

const isWinRatePositive = (value) => {
  const n = parseFloat(value);
  if (Number.isNaN(n)) return null;
  return n >= 50;
};

const loadWallets = () => {
  try {
    const saved = localStorage.getItem('solana-wallets');
    return saved ? JSON.parse(saved) : [];
  } catch { return []; }
};

const saveWallets = (wallets) => {
  try { localStorage.setItem('solana-wallets', JSON.stringify(wallets)); } catch {}
};

async function loadWalletsFromSupabase() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return null;
    const { data, error } = await supabase.from('wallets').select('*').eq('user_id', session.user.id).order('created_at', { ascending: true });
    if (error || !data) return null;
    return data.map(w => ({
      id: w.id,
      name: w.name,
      address: w.address,
      fullAddress: w.full_address,
      pnl: w.pnl, pnlPos: w.pnl_pos,
      tokens: w.tokens,
      roi: w.roi, roiPos: w.roi_pos,
      winRate: w.win_rate, winRatePos: w.win_rate_pos,
      status: w.status,
      favorite: w.favorite,
      tags: w.tags || [],
      note: w.note || '',
    }));
  } catch(e) { console.warn('Erreur chargement wallets:', e.message); return null; }
}

async function saveWalletToSupabase(wallet) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    await supabase.from('wallets').upsert({
      id: typeof wallet.id === 'string'? wallet.id : undefined,
      user_id: session.user.id,
      name: wallet.name,
      address: wallet.address,
      full_address: wallet.fullAddress,
      pnl: wallet.pnl, pnl_pos: wallet.pnlPos,
      tokens: wallet.tokens,
      roi: wallet.roi, roi_pos: wallet.roiPos,
      win_rate: wallet.winRate, win_rate_pos: wallet.winRatePos,
      status: wallet.status,
      favorite: wallet.favorite,
      tags: wallet.tags || [],
      note: wallet.note || '',
    });
  } catch(e) { console.warn('Erreur save wallet:', e.message); }
}

async function deleteWalletFromSupabase(id) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    if (typeof id === 'string') {
      await supabase.from('wallets').delete().eq('id', id).eq('user_id', session.user.id);
    }
  } catch(e) { console.warn('Erreur delete wallet:', e.message); }
}

const TAGS_OPTIONS = [
  { label:'🔥 Top',      color:'#ff6b35' },
  { label:'👀 Observer', color:'#7c4dff' },
  { label:'🤖 Bot',      color:'#f0a500' },
  { label:'💎 Diamant',  color:'#00e5cc' },
  { label:'⚠️ Risqué',   color:'#ff6b6b' },
];

export default function WalletsTab({ initialAddr, onInitialAddrUsed }) {
  const [wallets,        setWallets]        = useState(loadWallets);
  const [search,         setSearch]         = useState('');
  const [showModal,      setShowModal]      = useState(false);
  const [menuOpen,       setMenuOpen]       = useState(null);
  const [newName,        setNewName]        = useState('');
  const [newAddr,        setNewAddr]        = useState('');
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [activeFilter,   setActiveFilter]   = useState('Tous');
  const [editingId,      setEditingId]      = useState(null);
  const [editingName,    setEditingName]    = useState('');
  const [noteOpen,       setNoteOpen]       = useState(null);
  const [noteText,       setNoteText]       = useState('');

  useEffect(() => { saveWallets(wallets); }, [wallets]);

  // Auto-ouvre l'analyseur si une adresse est passée depuis SnipersTab
  useEffect(() => {
    if (!initialAddr) return;
    setSelectedWallet({ address: initialAddr.slice(0,8)+'...', fullAddress: initialAddr, name: 'Sniper', tags:[], note:'' });
    if (onInitialAddrUsed) onInitialAddrUsed();
  }, [initialAddr, onInitialAddrUsed]);

  //  Charger depuis Supabase au démarrage et fusionner avec localStorage
  useEffect(() => {
    loadWalletsFromSupabase().then(supabaseWallets => {
      const local = loadWallets();
      if (supabaseWallets !== null) {
        // Fusionner : Supabase en base + wallets locaux absents de Supabase
        const supabaseAddresses = new Set(supabaseWallets.map(w => ((w.fullAddress || w.address) || '').toLowerCase()));
        const localOnly = local.filter(w => {
          const key = ((w.fullAddress || w.address) || '').toLowerCase();
          return key && !supabaseAddresses.has(key);
        });
        const merged = [...supabaseWallets, ...localOnly];
        const seen = new Set();
        const deduped = merged.filter(w => {
          const key = ((w.fullAddress || w.address) || '').toLowerCase();
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        if (deduped.length > 0) {
          setWallets(deduped);
          localStorage.setItem('solana-wallets', JSON.stringify(deduped));
        }
      }
      const allIds = (supabaseWallets || local).map(w => typeof w.id === 'number' ? w.id : 0);
      if (allIds.length > 0) nextId = Math.max(...allIds) + 1;
    });
  }, []);const toggleFavorite = (id, e) => {
    e.stopPropagation();
    setWallets(prev => {
      const next = prev.map(w => w.id === id ? { ...w, favorite: !w.favorite } : w);
      const updated = next.find(w => w.id === id);
      if (updated) saveWalletToSupabase(updated);
      return next;
    });
  };

  const startRename = (w, e) => {
    e.stopPropagation();
    setEditingId(w.id);
    setEditingName(w.name);
    setMenuOpen(null);
  };

  const saveRename = (id) => {
    if (editingName.trim()) {
      setWallets(prev => {
        const next = prev.map(w => w.id === id ? { ...w, name: editingName.trim() } : w);
        const updated = next.find(w => w.id === id);
        if (updated) saveWalletToSupabase(updated);
        return next;
      });
    }
    setEditingId(null);
  };

  const openNote = (w, e) => {
    e.stopPropagation();
    setNoteOpen(w.id);
    setNoteText(w.note || '');
    setMenuOpen(null);
  };

  const saveNote = (id) => {
    setWallets(prev => {
      const next = prev.map(w => w.id === id ? { ...w, note: noteText } : w);
      const updated = next.find(w => w.id === id);
      if (updated) saveWalletToSupabase(updated);
      return next;
    });
    setNoteOpen(null);
  };

  const toggleTag = (id, tag, e) => {
    e.stopPropagation();
    setWallets(prev => {
      const next = prev.map(w => {
        if (w.id !== id) return w;
        const tags = w.tags || [];
        const has = tags.includes(tag);
        return { ...w, tags: has ? tags.filter(t => t !== tag) : [...tags, tag] };
      });
      const updated = next.find(w => w.id === id);
      if (updated) saveWalletToSupabase(updated);
      return next;
    });
  };

  const filtered = wallets.filter(w => {
    const matchSearch = !search ||
      w.name.toLowerCase().includes(search.toLowerCase()) ||
      w.address.toLowerCase().includes(search.toLowerCase());
    const matchFilter = activeFilter === 'Favoris'? !!w.favorite : true;
    return matchSearch && matchFilter;
  });

  const addWallet = async () => {
    if (!newAddr.trim()) return;
    const addr = newAddr.trim();
    const short = addr.length > 10 ? `${addr.slice(0,6)}...${addr.slice(-4)}`: addr;
    const newWallet = {
      favorite: false, tags: [], note: '',
      name: newName.trim() || `Wallet ${nextId++}`,
      address: short, fullAddress: addr,
      pnl: '+$0', pnlPos: true, tokens: 0,
      roi: '+0.00%', roiPos: true,
      winRate: '0%', winRatePos: false,
      status: 'pending',
    };
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data } = await supabase.from('wallets').insert({
          user_id: session.user.id,
          name: newWallet.name,
          address: newWallet.address,
          full_address: newWallet.fullAddress,
          pnl: newWallet.pnl, pnl_pos: newWallet.pnlPos,
          tokens: newWallet.tokens,
          roi: newWallet.roi, roi_pos: newWallet.roiPos,
          win_rate: newWallet.winRate, win_rate_pos: newWallet.winRatePos,
          status: newWallet.status,
          favorite: newWallet.favorite,
          tags: newWallet.tags,
          note: newWallet.note,
        }).select().single();
        if (data) newWallet.id = data.id;
      }
    } catch(e) { console.warn('Erreur add wallet:', e.message); }
    if (!newWallet.id) newWallet.id = nextId++;
    setWallets(prev => [...prev, newWallet]);
    setNewName(''); setNewAddr('');
    setShowModal(false);
  };

  const deleteWallet = (id) => {
    deleteWalletFromSupabase(id);
    setWallets(prev => prev.filter(w => w.id !== id));
    setMenuOpen(null);
  };

  const shortAddr = (addr) => (addr.length > 10 ? `${addr.slice(0,6)}...${addr.slice(-4)}`: addr);

  const upsertWalletFromAnalysis = ({ sourceWalletId, address, summary }) => {
    const fullAddress = (address || '').trim();
    if (!fullAddress) return;
    setWallets(prev => {
      let updated = false;
      const next = prev.map((w) => {
        const bySource  = sourceWalletId && w.id === sourceWalletId;
        const byAddress = (w.fullAddress || '').toLowerCase() === fullAddress.toLowerCase();
        if (!bySource && !byAddress) return w;
        updated = true;
        const updatedW = {
          ...w,
          address: shortAddr(fullAddress), fullAddress,
          pnl: summary.pnl, pnlPos: summary.pnlPos,
          tokens: summary.tokens,
          roi: summary.roi, roiPos: summary.roiPos,
          winRate: summary.winRate,
          winRatePos: isWinRatePositive(summary.winRate),
          status: 'done',
        };
        saveWalletToSupabase(updatedW);
        return updatedW;
      });
      if (updated) return next;
      const id = nextId++;
      const newW = {
        id, favorite: false, tags: [], note: '',
        name: `Wallet ${id}`,
        address: shortAddr(fullAddress), fullAddress,
        pnl: summary.pnl, pnlPos: summary.pnlPos,
        tokens: summary.tokens,
        roi: summary.roi, roiPos: summary.roiPos,
        winRate: summary.winRate,
        winRatePos: isWinRatePositive(summary.winRate),
        status: 'done',
      };
      saveWalletToSupabase(newW);
      return [...next, newW];
    });
  };

  //  Export CSV
  const exportCSV = () => {
    const rows = [
      ['Nom','Adresse','PNL','ROI','Win%','Tokens','Favori','Tags','Note'],
      ...wallets.map(w => [
        w.name, w.fullAddress||w.address, w.pnl, w.roi, w.winRate, w.tokens,
        w.favorite?'Oui':'Non',
        (w.tags||[]).join(''),
        (w.note||'').replace(/\n/g,''),
      ])
    ];
    const csv = rows.map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'wallets.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  if (selectedWallet) {
    return (
      <WalletAnalysisView
        wallet={selectedWallet}
        onBack={() => setSelectedWallet(null)}
        onAnalysisResolved={upsertWalletFromAnalysis}
        onRenameWallet={(id, name) => setWallets(prev => prev.map(w => w.id === id ? {...w, name} : w))}
      />
    );
  }

  const favCount = wallets.filter(w => w.favorite).length;return (
    <div className="wt-root"onClick={() => { setMenuOpen(null); }}>

      {/* Header */}
      <div className="wt-header">
        <div className="wt-header-left">
          <div className="wt-title-row">
            <span className="wt-arrow">&gt;</span>
            <h1 className="wt-title">WALLETS</h1>
          </div>
          <p className="wt-subtitle">Gérer et analyser les wallets Solana</p>
        </div>
        <div style={{display:'flex', gap:8, alignItems:'center'}}>
          <button onClick={exportCSV} style={{padding:'8px 14px', borderRadius:6, background:'rgba(0,229,204,0.08)', border:'1px solid #00e5cc33', color:'#00e5cc', fontSize:12, fontWeight:600, cursor:'pointer', display:'flex', alignItems:'center', gap:6}}>
            <svg viewBox="0 0 14 14"fill="none"width="11"height="11"><path d="M7 2v7M4 6l3 3 3-3M2 11h10"stroke="currentColor"strokeWidth="1.4"strokeLinecap="round"strokeLinejoin="round"/></svg>
            CSV
          </button>
          <button className="wt-new-btn"onClick={() => setShowModal(true)}>
            <svg viewBox="0 0 14 14"fill="none"width="12"height="12">
              <path d="M7 2v10M2 7h10"stroke="currentColor"strokeWidth="1.8"strokeLinecap="round"/>
            </svg>
            NOUVEAU WALLET
          </button>
        </div>
      </div>

      {/* Search + Filtres */}
      <div className="wt-search-wrap">
        <div className="wt-search-box">
          <svg viewBox="0 0 16 16"fill="none"width="13"height="13">
            <circle cx="7"cy="7"r="5"stroke="#555"strokeWidth="1.4"/>
            <path d="M11 11l3 3"stroke="#555"strokeWidth="1.4"strokeLinecap="round"/>
          </svg>
          <input className="wt-search-input"placeholder="Rechercher un wallet..."value={search} onChange={e => setSearch(e.target.value)}/>
        </div>
        <div style={{display:'flex', gap:6, marginLeft:12}}>
          {[{id:'Tous',label:`Tous (${wallets.length})`},{id:'Favoris',label:`Favoris (${favCount})`}].map(f=>(
            <button key={f.id} onClick={()=>setActiveFilter(f.id)} style={{padding:'6px 14px',borderRadius:6,fontSize:12,fontWeight:600,cursor:'pointer',background:activeFilter===f.id?'rgba(0,229,204,0.12)':'transparent',border:`1px solid ${activeFilter===f.id?'#00e5cc44':'#1a2030'}`,color:activeFilter===f.id?'#00e5cc':'#5a6a80',transition:'all 0.15s'}}>{f.label}</button>
          ))}
        </div>
      </div>

      {/* Wallet grid */}
      <div className="wt-grid">
        {filtered.length === 0 ? (
          <div className="wt-empty">
            <svg viewBox="0 0 40 40"fill="none"width="36"height="36"><circle cx="20"cy="20"r="16"stroke="#1e2530"strokeWidth="1.5"/><path d="M14 20h12M20 14v12"stroke="#2a3a50"strokeWidth="1.5"strokeLinecap="round"/></svg>
            <p>{activeFilter==='Favoris'?'Aucun wallet en favori':'Aucun wallet ajouté'}</p>
            {activeFilter==='Tous'&&(<button className="wt-new-btn"onClick={()=>setShowModal(true)}>+ Ajouter un wallet</button>)}
          </div>
        ) : (
          filtered.map(w => (
            <div key={w.id} className="wt-card"onClick={e => { e.stopPropagation(); if(editingId===w.id||noteOpen===w.id) return; setSelectedWallet(w); }}>
              <div className="wt-card-top">
                <div style={{flex:1, minWidth:0}}>
                  {editingId === w.id ? (
                    <input
                      autoFocus
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onBlur={() => saveRename(w.id)}
                      onKeyDown={e => { if(e.key==='Enter') saveRename(w.id); if(e.key==='Escape') setEditingId(null); }}
                      onClick={e => e.stopPropagation()}
                      style={{background:'#1a2030',border:'1px solid #00e5cc44',borderRadius:4,color:'#e0e4ec',fontSize:13,fontWeight:700,padding:'3px 8px',width:'100%',outline:'none'}}
                    />
                  ) : (
                    <div className="wt-card-name"onDoubleClick={e => startRename(w, e)} title="Double-clic pour renommer">{w.name}</div>
                  )}
                  <div className="wt-card-addr">{w.address}</div>
                  {(w.tags||[]).length > 0 && (
                    <div style={{display:'flex',gap:4,marginTop:4,flexWrap:'wrap'}}>
                      {(w.tags||[]).map(tag => {
                        const t = TAGS_OPTIONS.find(t=>t.label===tag);
                        return <span key={tag} style={{fontSize:10,padding:'1px 6px',borderRadius:10,background:`${t?.color||'#5a6a80'}22`,color:t?.color||'#5a6a80',border:`1px solid ${t?.color||'#5a6a80'}44`}}>{tag}</span>;
                      })}
                    </div>
                  )}
                  {w.note && <div style={{fontSize:10,color:'#5a6a80',marginTop:4,fontStyle:'italic',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:200}}> {w.note}</div>}
                </div>
                <div style={{display:'flex', alignItems:'center', gap:6}}>
                  <button onClick={e=>toggleFavorite(w.id,e)} title={w.favorite?'Retirer des favoris':'Ajouter aux favoris'} style={{background:'none',border:'none',cursor:'pointer',padding:'4px',fontSize:16,lineHeight:1,color:w.favorite?'#f0c040':'#2a3a50',transition:'color 0.15s, transform 0.15s',transform:w.favorite?'scale(1.15)':'scale(1)'}}>
                    {w.favorite?'★':'☆'}
                  </button>
                  <div className="wt-menu-wrap">
                    <button className="wt-menu-btn"onClick={e=>{e.stopPropagation();setMenuOpen(menuOpen===w.id?null:w.id);}}>
                      <span/><span/><span/>
                    </button>
                    {menuOpen === w.id && (
                      <div className="wt-dropdown">
                        <button className="wt-dd-item"onClick={e=>startRename(w,e)}>
                          <svg viewBox="0 0 14 14"fill="none"width="11"height="11"><path d="M2 10l8-8 2 2-8 8H2v-2z"stroke="currentColor"strokeWidth="1.2"strokeLinejoin="round"/></svg>
                          Renommer
                        </button>
                        <button className="wt-dd-item"onClick={e=>openNote(w,e)}>
                          <svg viewBox="0 0 14 14"fill="none"width="11"height="11"><rect x="2"y="2"width="10"height="10"rx="1.5"stroke="currentColor"strokeWidth="1.2"/><path d="M4 5h6M4 7h6M4 9h4"stroke="currentColor"strokeWidth="1.2"strokeLinecap="round"/></svg>
                          {w.note ? 'Modifier la note': 'Ajouter une note'}
                        </button>
                        <button className="wt-dd-item"onClick={e=>{e.stopPropagation();toggleFavorite(w.id,e);setMenuOpen(null);}}>
                          <svg viewBox="0 0 14 14"fill="none"width="11"height="11"><path d="M7 1l1.5 4H13l-3.5 2.5 1.3 4L7 9 3.2 11.5l1.3-4L1 5h4.5z"stroke="currentColor"strokeWidth="1.2"strokeLinejoin="round"/></svg>
                          {w.favorite?'Retirer des favoris':'Ajouter aux favoris'}
                        </button>
                        <button className="wt-dd-item danger"onClick={e=>{e.stopPropagation();deleteWallet(w.id);}}>
                          <svg viewBox="0 0 14 14"fill="none"width="11"height="11"><path d="M2 4h10M5 4V3h4v1M5 6v4M9 6v4M3 4l1 8h6l1-8"stroke="currentColor"strokeWidth="1.2"strokeLinecap="round"strokeLinejoin="round"/></svg>
                          Supprimer
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {menuOpen === w.id && (
                <div onClick={e=>e.stopPropagation()} style={{marginTop:8,display:'flex',gap:4,flexWrap:'wrap',padding:'8px',background:'#0a0d14',borderRadius:6,border:'1px solid #1a2030'}}>
                  <div style={{fontSize:10,color:'#5a6a80',width:'100%',marginBottom:4}}>Tags :</div>
                  {TAGS_OPTIONS.map(t=>(
                    <button key={t.label} onClick={e=>toggleTag(w.id,t.label,e)} style={{fontSize:10,padding:'2px 8px',borderRadius:10,cursor:'pointer',background:(w.tags||[]).includes(t.label)?`${t.color}22`:'transparent',border:`1px solid ${(w.tags||[]).includes(t.label)?t.color:'#1a2030'}`,color:(w.tags||[]).includes(t.label)?t.color:'#5a6a80'}}>
                      {t.label}
                    </button>
                  ))}
                </div>
              )}

              {noteOpen === w.id && (
                <div onClick={e=>e.stopPropagation()} style={{marginTop:8,padding:10,background:'#0a0d14',borderRadius:6,border:'1px solid #1a2030'}}>
                  <textarea
                    autoFocus
                    value={noteText}
                    onChange={e=>setNoteText(e.target.value)}
                    placeholder="Ajoute une note sur ce wallet..."style={{width:'100%',background:'#0d1117',border:'1px solid #1a2030',borderRadius:4,color:'#c0c8d8',fontSize:11,padding:'6px 8px',outline:'none',resize:'vertical',minHeight:60,boxSizing:'border-box'}}
                  />
                  <div style={{display:'flex',gap:6,marginTop:6,justifyContent:'flex-end'}}>
                    <button onClick={()=>setNoteOpen(null)} style={{padding:'4px 10px',borderRadius:4,background:'transparent',border:'1px solid #1a2030',color:'#5a6a80',fontSize:11,cursor:'pointer'}}>Annuler</button>
                    <button onClick={()=>saveNote(w.id)} style={{padding:'4px 10px',borderRadius:4,background:'rgba(0,229,204,0.12)',border:'1px solid #00e5cc44',color:'#00e5cc',fontSize:11,fontWeight:700,cursor:'pointer'}}>Sauvegarder</button>
                  </div>
                </div>
              )}

              <div className="wt-stats">
                <div className="wt-stat"><div className="wt-stat-lbl">PNL</div><div className={`wt-stat-val ${w.pnlPos?'pos':'neg'}`}>{w.pnl}</div></div>
                <div className="wt-stat"><div className="wt-stat-lbl">TOKENS</div><div className="wt-stat-val">{w.tokens}</div></div>
                <div className="wt-stat"><div className="wt-stat-lbl">ROI</div><div className={`wt-stat-val ${w.roiPos?'pos':'neg'}`}>{w.roi}</div></div>
                <div className="wt-stat"><div className="wt-stat-lbl">WIN%</div><div className={`wt-stat-val ${w.winRatePos===null?'':w.winRatePos?'pos':'neg'}`}>{w.winRate}</div></div>
              </div>

              <div className="wt-card-footer">
                <span className={`wt-status ${w.status}`}>{w.status==='done'?'Analyse terminée':"En attente d'analyse"}</span>
                <button className="wt-unknown-btn">UNKNOWN</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="wt-overlay"onClick={() => setShowModal(false)}>
          <div className="wt-modal"onClick={e => e.stopPropagation()}>
            <div className="wt-modal-titlebar">
              <div className="wt-traffic-lights"><span className="wt-tl red"/><span className="wt-tl yellow"/><span className="wt-tl green"/></div>
              <span className="wt-modal-exe">ADD_WALLET.EXE</span>
              <button className="wt-modal-close"onClick={() => setShowModal(false)}><svg viewBox="0 0 14 14"fill="none"width="10"height="10"><path d="M2 2l10 10M12 2L2 12"stroke="currentColor"strokeWidth="1.5"strokeLinecap="round"/></svg></button>
            </div>
            <div className="wt-modal-body">
              <div className="wt-field">
                <label>ADRESSE DU WALLET</label>
                <div className="wt-input-wrap">
                  <svg className="wt-input-icon"viewBox="0 0 16 16"fill="none"width="13"height="13"><rect x="2"y="4"width="12"height="9"rx="1.5"stroke="currentColor"strokeWidth="1.3"/><path d="M2 7h12"stroke="currentColor"strokeWidth="1.3"/><circle cx="5"cy="10"r="1"fill="currentColor"/></svg>
                  <input value={newAddr} onChange={e=>setNewAddr(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addWallet()} placeholder="Entrez l'adresse Solana (44 caractères)"autoFocus/>
                </div>
              </div>
              <div className="wt-field">
                <label>SURNOM (OPTIONNEL)</label>
                <div className="wt-input-wrap">
                  <span className="wt-input-tag"/>
                  <input value={newName} onChange={e=>setNewName(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addWallet()} placeholder="ex: Wallet Principal"/>
                </div>
              </div>
              <div className="wt-info-box">
                <svg viewBox="0 0 16 16"fill="none"width="14"height="14"style={{flexShrink:0}}><circle cx="8"cy="8"r="6.5"stroke="#00e5cc"strokeWidth="1.3"/><path d="M8 7v4"stroke="#00e5cc"strokeWidth="1.4"strokeLinecap="round"/><circle cx="8"cy="5.5"r="0.8"fill="#00e5cc"/></svg>
                <div>
                  <div className="wt-info-title">ANALYSE INSTANTANÉE</div>
                  <div className="wt-info-desc">Analyse instantanée du wallet. Vous serez redirigé vers la page de résultats.</div>
                </div>
              </div>
            </div>
            <div className="wt-modal-actions">
              <button className="wt-btn-cancel"onClick={() => setShowModal(false)}>ANNULER</button>
              <button className="wt-btn-add"onClick={addWallet}>ANALYSER</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}